import React from 'react';
import {TripResponse} from '../../store/backendApi';
import {useNavigate} from 'react-router-dom';
import {ArrowsUpDownIcon, CalendarIcon, ChatBubbleLeftRightIcon, TrashIcon,} from '@heroicons/react/24/outline';
import {Button} from '../../components/Button';
import {useHeaderOptions} from '../../hooks/editTrip/useHeaderOptions';
import {DateRangePicker} from '../../components/inputs/DateRangePicker';

export interface HeaderOptionsPageProps {
  tripData: TripResponse;
}

export const HeaderOptionsPage = (props: HeaderOptionsPageProps) => {
  const navigate = useNavigate();

  // All business logic is now in the hook
  const {
    tripStartDate,
    tripEndDate,
    tripSortOrder,
    interactionsEnabled,
    setTripDates,
    setTripSortOrder,
    setInteractionsEnabled,
    updateTrip,
    deleteTrip,
    reset,
    hasChanges,
    patchTripLoading,
    deleteTripLoading,
    deleteConfirmation,
    setDeleteConfirmation,
  } = useHeaderOptions(props.tripData);

  const handleDelete = async () => {
    await deleteTrip(() => navigate('/home'));
  };

  return (
    <div className="space-y-5">
      {/* Info Note */}
      <div className="bg-canvas border border-muted rounded-lg p-4">
        <p className="text-sm text-muted">
          <strong className="text-main">Tip:</strong> Trip title, description, and cover image can
          now be edited directly on the page header!
        </p>
      </div>

      {/* Trip Dates */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm  text-main">
          <CalendarIcon className="w-4 h-4 text-brand" />
          When is your trip?
        </label>
        <DateRangePicker
          startDate={tripStartDate}
          endDate={tripEndDate}
          onChange={setTripDates}
          startDateId="trip-start-date"
          endDateId="trip-end-date"
        />
      </div>

      {/* Story Sort Order */}
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm  text-main">
          <ArrowsUpDownIcon className="w-4 h-4 text-brand" />
          Story Order
        </label>
        <div className="space-y-3 pl-1">
          {/* DESC Option - Newest First */}
          <div className="relative flex items-start">
            <div className="flex h-6 items-center">
              <input
                id="sort-desc"
                name="sort-order"
                type="radio"
                checked={tripSortOrder === 'DESC'}
                onChange={() => setTripSortOrder('DESC')}
                className="h-4 w-4 border-muted text-brand focus:ring-brand"
              />
            </div>
            <div className="ml-3 text-sm leading-6">
              <label htmlFor="sort-desc" className=" text-main">
                Newest First
              </label>
              <p className="text-muted">Stories sorted from most recent to oldest</p>
            </div>
          </div>

          {/* ASC Option - Oldest First */}
          <div className="relative flex items-start">
            <div className="flex h-6 items-center">
              <input
                id="sort-asc"
                name="sort-order"
                type="radio"
                checked={tripSortOrder === 'ASC'}
                onChange={() => setTripSortOrder('ASC')}
                className="h-4 w-4 border-muted text-brand focus:ring-brand"
              />
            </div>
            <div className="ml-3 text-sm leading-6">
              <label htmlFor="sort-asc" className=" text-main">
                Oldest First
              </label>
              <p className="text-muted">Stories sorted chronologically from start to finish</p>
            </div>
          </div>
        </div>
      </div>

      {/* Interactions Enabled */}
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm  text-main">
          <ChatBubbleLeftRightIcon className="w-4 h-4 text-brand" />
          Viewer Interactions
        </label>
        <div className="pl-1">
          <div className="relative flex items-start">
            <div className="flex h-6 items-center">
              <input
                id="interactions-enabled"
                name="interactions-enabled"
                type="checkbox"
                checked={interactionsEnabled}
                onChange={(e) => setInteractionsEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-muted text-brand focus:ring-brand"
              />
            </div>
            <div className="ml-3 text-sm leading-6">
              <label htmlFor="interactions-enabled" className=" text-main">
                Enable Likes and Comments
              </label>
              <p className="text-muted">Allow viewers to like and comment on your stories</p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="pt-3 space-y-3 border-t border-muted">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="primary"
            className="flex-1"
            disabled={patchTripLoading || !hasChanges}
            loading={patchTripLoading}
            onClick={() => updateTrip()}
          >
            {patchTripLoading ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button type="button" variant="secondary" onClick={reset} disabled={!hasChanges}>
            Reset
          </Button>
        </div>

        {/* Delete Trip Button */}
        {deleteConfirmation ? (
          <div className="space-y-2">
            <p className="text-sm text-error  text-center flex items-center justify-center gap-2">
              <TrashIcon className="w-4 h-4" />
              Are you sure? This cannot be undone!
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="destructive"
                className="flex-1"
                disabled={deleteTripLoading}
                loading={deleteTripLoading}
                onClick={handleDelete}
              >
                {deleteTripLoading ? 'Deleting...' : 'Yes, Delete Trip'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setDeleteConfirmation(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant="destructive"
            fullWidth
            disabled={deleteTripLoading}
            onClick={handleDelete}
            iconBefore={<TrashIcon className="w-4 h-4" />}
          >
            Delete Trip
          </Button>
        )}
      </div>
    </div>
  );
};
